import React, { useState } from "react";
import { Layout, Tag } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";

const { Header, Content } = Layout;

const pageMeta = {
  "/": {
    title: "Dashboard",
    subtitle: "Live charger status | Energy | Sessions | OCPP Health",
  },
  "/chargers": {
    title: "Chargers",
    subtitle: "Live charger | Connector | Heartbeat | SOC monitoring",
  },
  "/sessions": {
    title: "Live Sessions",
    subtitle: "Charging sessions | Energy | SOC | Transaction status",
  },
  "/alerts": {
    title: "Alerts",
    subtitle: "Faults | Missing STOP | SOC missing | Heartbeat issues",
  },
  "/reports": {
    title: "Reports",
    subtitle: "Daily | Monthly | Export data",
  },
  "/debug": {
    title: "OCPP Debug",
    subtitle: "Raw OCPP logs | Message timeline | Protocol diagnostics",
  },
  "/admin": {
    title: "Administration",
    subtitle: "Users | Roles | Depot | Charger configuration",
  },
  "/support": {
    title: "Support",
    subtitle: "Helpdesk | Tickets | System support",
  },
};

export default function AppShell({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const pathname = location.pathname;

  const basePath =
    pathname.startsWith("/chargers/")
      ? "/chargers"
      : pathname.startsWith("/alerts/")
      ? "/alerts"
      : pathname.startsWith("/reports/")
      ? "/reports"
      : pathname;

  const meta = pageMeta[basePath] || pageMeta["/"];
  const showDashboardStatus = pathname === "/";

  const user = localStorage.getItem("navedge_user") || "admin@exxomatic.in";
  const role = localStorage.getItem("navedge_role") || "Admin";

  const latestTime = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <Layout style={{ minHeight: "100vh", background: "#f3f6fb" }}>
      <Sidebar collapsed={collapsed} />

      <Layout>
        <Header style={styles.header}>
          <div style={styles.left}>
            <span
              onClick={() => setCollapsed(!collapsed)}
              style={styles.collapseIcon}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>

            <div style={styles.titleWrap}>
              <div style={styles.title}>{meta.title}</div>
              <div style={styles.subtitle}>{meta.subtitle}</div>
            </div>
          </div>

          <div style={styles.right}>
            {showDashboardStatus && (
              <>
                <span style={styles.updated}>{latestTime}</span>
                <span style={styles.liveDot}>● LIVE</span>
              </>
            )}

            <div style={styles.userBox}>
              <UserOutlined style={{ color: "#0f3b82" }} />
              <span style={styles.userText}>{user}</span>
              <Tag color="blue" style={{ marginLeft: 6 }}>
                {role}
              </Tag>
            </div>
          </div>
        </Header>

        <Content style={styles.content}>{children}</Content>
      </Layout>

      <style>
        {`
          @keyframes softBlink {
            0% { opacity: 0.45; }
            50% { opacity: 1; }
            100% { opacity: 0.45; }
          }
        `}
      </style>
    </Layout>
  );
}

const styles = {
  header: {
    height: 76,
    background: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    padding: "0 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  left: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  collapseIcon: {
    fontSize: 18,
    cursor: "pointer",
    color: "#334155",
  },
  titleWrap: {
    borderLeft: "2px solid #cbd5e1",
    paddingLeft: 14,
    lineHeight: 1.2,
  },
  title: {
    fontSize: 22,
    fontWeight: 800,
    color: "#0f3b82",
  },
  subtitle: {
    fontSize: 13,
    color: "#64748b",
    marginTop: 4,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  updated: {
    fontSize: 13,
    fontWeight: 500,
    color: "#64748b",
  },
  liveDot: {
    fontSize: 12,
    fontWeight: 700,
    color: "#2563eb",
    opacity: 0.75,
    animation: "softBlink 2.8s infinite",
  },
  userBox: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    paddingLeft: 14,
    borderLeft: "1px solid #e5e7eb",
  },
  userText: {
    fontSize: 13,
    fontWeight: 600,
    color: "#334155",
  },
  content: {
    padding: 24,
    background: "#f3f6fb",
    minHeight: "calc(100vh - 76px)",
  },
};